import { type Ref } from 'vue';
import { ref, watch } from 'vue';
import { getAccountsStats, getNegotiationsStats, getPaymentsStats } from '@/services/dashboardService';
import { useToast } from 'primevue/usetoast';

// PHASE1: matches the trimmed getAccountsStats return shape.
interface AccountsStatsData {
    totalAccounts: number;
}
/*
 * PHASE2: restore alongside dashboardService.ts's PHASE2 getAccountsStats.
interface AccountsStatsData {
    verifiedAccounts: number;
    pendingAccounts: number;
}
*/

interface NegotiationsStatsData {
    eligibleAccounts: number;
    pendingOffers: number;
    acceptedOffers: number;
    rejectedOffers: number;
    completedNegotiations: number;
}

interface PaymentsStatsData {
    totalClearedTransactions: number;
    totalClearedAmount: number;
    pendingTransactions: number;
    openTransactions: number;
    lowBalanceTransactions: number;
}

export interface DashboardDataOptions {
    loadAccounts?: boolean;
    loadNegotiations?: boolean;
    loadPayments?: boolean;
}

function getErrorMessage(error: unknown, fallback: string): string {
    if (typeof error !== 'object' || error === null || !('message' in error)) {
        return fallback;
    }

    const { message } = error as { message?: unknown };
    return typeof message === 'string' && message !== '' ? message : fallback;
}

export const useDashboardData = (
    dateRange: Ref<[string, string] | null>,
    options: DashboardDataOptions = {},
) => {
    const { loadAccounts = true, loadNegotiations = true, loadPayments = true } = options;
    const toast = useToast();

    const accountsData = ref<AccountsStatsData | null>(null);
    const accountsLoading = ref(true);

    const negotiationsData = ref<NegotiationsStatsData | null>(null);
    const negotiationsLoading = ref(true);

    const paymentsData = ref<PaymentsStatsData | null>(null);
    const paymentsLoading = ref(true);

    const loadAccountsStats = async (startDate: string, endDate: string) => {
        accountsLoading.value = true;
        try {
            accountsData.value = await getAccountsStats(startDate, endDate);
        } catch (error) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: getErrorMessage(error, 'Failed to fetch accounts stats'),
                life: 3000,
            });
        } finally {
            accountsLoading.value = false;
        }
    };

    const loadNegotiationsStats = async (startDate: string, endDate: string) => {
        negotiationsLoading.value = true;
        try {
            negotiationsData.value = await getNegotiationsStats(startDate, endDate);
        } catch (error) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: getErrorMessage(error, 'Failed to fetch negotiations stats'),
                life: 3000,
            });
        } finally {
            negotiationsLoading.value = false;
        }
    };

    const loadPaymentsStats = async (startDate: string, endDate: string) => {
        paymentsLoading.value = true;
        try {
            paymentsData.value = await getPaymentsStats(startDate, endDate);
        } catch (error) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: getErrorMessage(error, 'Failed to fetch payments stats'),
                life: 3000,
            });
        } finally {
            paymentsLoading.value = false;
        }
    };

    const fetchDashboardData = () => {
        const range = dateRange.value;
        if (!range) return;
        const [startDate, endDate] = range;

        if (loadAccounts) loadAccountsStats(startDate, endDate);
        if (loadNegotiations) loadNegotiationsStats(startDate, endDate);
        if (loadPayments) loadPaymentsStats(startDate, endDate);
    };

    watch(dateRange, fetchDashboardData, { immediate: true });

    return {
        accountsData,
        accountsLoading,
        negotiationsData,
        negotiationsLoading,
        paymentsData,
        paymentsLoading,
    };
};
